import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  getLineItemGroupingKey,
  groupFulfillmentOrderLineItems,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { getSerialNumbersByLineItemGroupingKey } from "@redotech/redo-fulfillment-group-common/scan-serial-numbers";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import {
  ItemTableHeader,
  ProductListRow,
} from "@redotech/redo-web/product-list-row";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { CurrentInventoryButton } from "../../common/current-inventory-button";
import { LineItemLots } from "../../common/line-item-lots";
import { SplitGroupButton } from "../../common/split-group-button";
import * as styles from "./outbound-order.module.css";

export const ItemsCard = memo(function ItemsCard({
  fulfillmentGroup,
  triggerReload,
  everythingDisabled,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  triggerReload: () => Promise<void>;
  everythingDisabled: boolean;
}) {
  const lineItemsQuantity = useMemo(() => {
    return fulfillmentGroup.items.reduce((acc, item) => acc + item.quantity, 0);
  }, [fulfillmentGroup]);

  return (
    <Flex
      bgColor="primary"
      borderRadius="lg"
      dir="column"
      flexDirection="column"
      gap="none"
      w="full"
    >
      <Flex
        borderColor="secondary"
        borderStyle="solid"
        borderWidth="1px"
        className={styles.flatBottomCorners}
        justify="space-between"
        p="3xl"
      >
        <Flex flexDirection="column" justify="center">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Items in this fulfillment ({lineItemsQuantity})
          </Text>
        </Flex>
        <Flex align="center" gap="sm">
          <CurrentInventoryButton fulfillmentGroups={[fulfillmentGroup]} />
          <SplitGroupButton
            disabled={everythingDisabled}
            fulfillmentGroup={fulfillmentGroup}
            isSlideout={false}
            size="sm"
            triggerReload={triggerReload}
          />
        </Flex>
      </Flex>
      <ItemsCardTable fulfillmentGroup={fulfillmentGroup} />
    </Flex>
  );
});

export const ItemsCardTable = memo(function ItemsCardTable({
  fulfillmentGroup,
}: {
  fulfillmentGroup: FulfillmentGroupData;
}) {
  const team = useContext(TeamContext);
  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;
  const hideUnderscoreCustomAttributes =
    !!team?.settings?.outboundLabels?.hideUnderscoreCustomAttributes;
  const items = useMemo(() => {
    return groupFulfillmentOrderLineItems(fulfillmentGroup.items, {
      degroupByCustomAttributes: showCustomAttributes,
      preferRedoProductId: true,
    });
  }, [fulfillmentGroup, showCustomAttributes]);

  const serialNumbersByGroupingKey = useMemo(() => {
    return getSerialNumbersByLineItemGroupingKey(
      fulfillmentGroup.items,
      fulfillmentGroup.scanVerifiedItemized,
      {
        degroupByCustomAttributes: showCustomAttributes,
        preferRedoProductId: true,
      },
    );
  }, [fulfillmentGroup, showCustomAttributes]);

  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      className={styles.flatTopCorners}
      dir="column"
      gap="xs"
      height="fit-content"
      w="full"
    >
      <ItemTableHeader />
      <Flex dir="column" flex="1" gap="md" w="full">
        {items.map((item, index) => (
          <Flex
            borderBottomWidth={index !== items.length - 1 ? "1px" : "none"}
            borderColor="secondary"
            borderStyle="solid"
            dir="column"
            gap="none"
            key={index}
            w="full"
          >
            <ProductListRow
              customAttributes={
                showCustomAttributes
                  ? hideUnderscoreCustomAttributes
                    ? item.customAttributes?.filter(
                        (attr) => !attr.key.startsWith("_"),
                      )
                    : item.customAttributes
                  : undefined
              }
              imageTooltipPlacement="top"
              imageUrl={item.imageUrl}
              price={item.unitDiscountedPrice.amount}
              priceMoney={toMoney(item.unitDiscountedPrice)}
              quantity={item.quantity}
              serialNumbers={serialNumbersByGroupingKey.get(
                getLineItemGroupingKey(item, {
                  degroupByCustomAttributes: showCustomAttributes,
                }),
              )}
              sku={item.sku}
              title={item.productTitle || item.title}
              variantTitle={item.variantTitle}
            />
            {item.lots?.length ? (
              <Flex pb="lg" px="xl" w="full">
                <LineItemLots lots={item.lots} />
              </Flex>
            ) : null}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
});
